import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';
import config from '../config/environment';
import axios from 'axios';

const SocketContext = createContext();

export const useSocket = () => {
    return useContext(SocketContext);
};

export const SocketProvider = ({ children }) => {
    const [socket, setSocket] = useState(null);
    const [unreadCount, setUnreadCount] = useState(0);
    const { user } = useAuth();
    const socketRef = useRef(null);

    // Initialize Socket
    useEffect(() => {
        if (user && !socketRef.current) {
            const newSocket = io(config.WEBSOCKET.MAIN.URL, {
                query: { userId: user.id },
                // No path option - use default /socket.io/ (root namespace)
                reconnection: true,
            });

            newSocket.on('connect', () => {
                console.log('✅ Global Socket Connected:', newSocket.id);
            });

            newSocket.on('receiveMessage', (message) => {
                // Increment unread count if message is for us and we are not the sender
                if (message.receiverId === user.id) {
                    // We could optimize this by checking if we are currently looking at this chat
                    // But for now, simple increment is fine. Ideally, ChatComponent will resetting 
                    // the count or specific chat read status when opened.

                    // Fetch fresh count to be accurate (since we track total unread conversations)
                    fetchUnreadCount();
                }
            });

            socketRef.current = newSocket;
            setSocket(newSocket);
        }

        // Cleanup on logout
        if (!user && socketRef.current) {
            socketRef.current.disconnect();
            socketRef.current = null;
            setSocket(null);
        }

    }, [user]);

    // Fetch Unread Count
    const fetchUnreadCount = async () => {
        if (!user) return;
        try {
            const res = await axios.get(`${config.REACT_APP_BASE_URL}/chats/unread-count`, {
                withCredentials: true
            });
            if (res.data.success) {
                setUnreadCount(res.data.count);
            }
        } catch (err) {
            console.error("Error fetching unread count:", err);
        }
    };

    useEffect(() => {
        fetchUnreadCount();
    }, [user]);

    // Provide a way to manually update count (e.g. when opening a chat)
    const updateUnreadCount = () => {
        fetchUnreadCount();
    };

    return (
        <SocketContext.Provider value={{ socket, unreadCount, updateUnreadCount }}>
            {children}
        </SocketContext.Provider>
    );
};
